import {
  Constants,
  directionList,
  durationList,
  marketList,
  patternSpecificationList,
} from '@hvf/contracts'
import dayjs from 'dayjs'
import * as v from 'valibot'

// Helper for fields that can be empty strings, missing entirely, or null
const permissiveString = v.optional(v.nullable(v.string('Invalid text format')))

// ============================================================================
// General Schema
// ============================================================================
export const GeneralSchema = v.object({
  id: permissiveString,
  created_by: permissiveString,
  duration: v.optional(v.picklist(durationList, 'Please select a valid duration')),
  symbol: v.optional(v.nullable(v.pipe(v.string('Symbol must be text'), v.nonEmpty('Please enter a symbol')))),
  title: v.optional(v.nullable(v.pipe(v.string('Title must be text'), v.nonEmpty('Please enter a title')))),
  position: v.optional(v.nullable(v.union([v.picklist(directionList, 'Invalid position')]))),
  description: permissiveString,
  market: v.optional(v.picklist(marketList, 'Please select a valid market')),
  stage: v.optional(v.picklist([...Constants.public.Enums.stage], 'Please select a valid stage')),
  exchange: v.optional(v.nullable(v.pipe(v.string('Exchange must be text'), v.nonEmpty('Please enter an exchange')))),
  image_macro: permissiveString,
  image_pattern: permissiveString,
  image_trend: permissiveString,
  image_trigger: permissiveString,
  image_time_stop: permissiveString,
  image_volume_by_price: permissiveString,
  first_in_new_trend: v.optional(v.nullable(v.boolean('Must be a boolean value'))),
  has_primer: v.optional(v.nullable(v.boolean('Must be a boolean value'))),
  trigger_date: v.optional(v.nullable(v.any())),
})

// ============================================================================
// Technical Schema
// ============================================================================
export const TechnicalSchema = v.pipe(
  v.partial(
    v.object({
      high_1_price: v.nullable(v.pipe(v.number('Please enter a valid price'), v.custom<number>((input) => input !== 0, 'Price cannot be 0'))),
      high_1_time: v.nullable(v.pipe(v.string('Please select a valid time'), v.custom<string>((input) => input !== '0', 'Invalid timestamp'))),
      high_2_price: v.nullable(v.pipe(v.number('Please enter a valid price'), v.custom<number>((input) => input !== 0, 'Price cannot be 0'))),
      high_2_time: v.nullable(v.pipe(v.string('Please select a valid time'), v.custom<string>((input) => input !== '0', 'Invalid timestamp'))),
      high_3_price: v.nullable(v.pipe(v.number('Please enter a valid price'), v.custom<number>((input) => input !== 0, 'Price cannot be 0'))),
      high_3_time: v.nullable(v.pipe(v.string('Please select a valid time'), v.custom<string>((input) => input !== '0', 'Invalid timestamp'))),
      low_1_price: v.nullable(v.pipe(v.number('Please enter a valid price'), v.custom<number>((input) => input !== 0, 'Price cannot be 0'))),
      low_1_time: v.nullable(v.pipe(v.string('Please select a valid time'), v.custom<string>((input) => input !== '0', 'Invalid timestamp'))),
      low_2_price: v.nullable(v.pipe(v.number('Please enter a valid price'), v.custom<number>((input) => input !== 0, 'Price cannot be 0'))),
      low_2_time: v.nullable(v.pipe(v.string('Please select a valid time'), v.custom<string>((input) => input !== '0', 'Invalid timestamp'))),
      low_3_price: v.nullable(v.pipe(v.number('Please enter a valid price'), v.custom<number>((input) => input !== 0, 'Price cannot be 0'))),
      low_3_time: v.nullable(v.pipe(v.string('Please select a valid time'), v.custom<string>((input) => input !== '0', 'Invalid timestamp'))),
    })
  ),

  // --- PRICE VALIDATION (The Y-Axis) ---
  v.partialCheck(
    [['high_1_price'], ['high_2_price']],
    (input) => {
      if (input.high_1_price == null || input.high_2_price == null) return true
      return input.high_1_price > input.high_2_price
    },
    'HVF_ERROR: High 1 price must be greater than High 2 price',
  ),
  v.partialCheck(
    [['high_2_price'], ['high_3_price']],
    (input) => {
      if (input.high_2_price == null || input.high_3_price == null) return true
      return input.high_2_price > input.high_3_price
    },
    'HVF_ERROR: High 2 price must be greater than High 3 price',
  ),

  v.partialCheck(
    [['low_1_price'], ['low_2_price']],
    (input) => {
      if (input.low_1_price == null || input.low_2_price == null) return true
      return input.low_1_price < input.low_2_price
    },
    'HVF_ERROR: Low 1 price must be less than Low 2 price',
  ),
  v.partialCheck(
    [['low_2_price'], ['low_3_price']],
    (input) => {
      if (input.low_2_price == null || input.low_3_price == null) return true
      return input.low_2_price < input.low_3_price
    },
    'HVF_ERROR: Low 2 price must be less than Low 3 price',
  ),

  v.partialCheck(
    [['high_3_price'], ['low_3_price']],
    (input) => {
      if (input.high_3_price == null || input.low_3_price == null) return true
      return input.high_3_price > input.low_3_price
    },
    'HVF_ERROR: High 3 price must remain greater than Low 3 price',
  ),

  // --- TIME VALIDATION (The X-Axis) ---
  v.partialCheck(
    [['high_1_time'], ['high_2_time']],
    (input) => validateTime(input.high_1_time, input.high_2_time),
    'HVF_ERROR: High 1 time must be before or the same as High 2 time',
  ),
  v.partialCheck(
    [['high_2_time'], ['high_3_time']],
    (input) => validateTime(input.high_2_time, input.high_3_time),
    'HVF_ERROR: High 2 time must be before or the same as High 3 time',
  ),

  v.partialCheck(
    [['low_1_time'], ['low_2_time']],
    (input) => validateTime(input.low_1_time, input.low_2_time),
    'HVF_ERROR: Low 1 time must be before or the same as Low 2 time',
  ),
  v.partialCheck(
    [['low_2_time'], ['low_3_time']],
    (input) => validateTime(input.low_2_time, input.low_3_time),
    'HVF_ERROR: Low 2 time must be before or the same as Low 3 time',
  ),
)

const validateTime = (timeA?: string | null, timeB?: string | null) => {
  if (!timeA || !timeB) return true
  return !dayjs(timeA).isAfter(dayjs(timeB))
}

// ============================================================================
// Computed Schema
// ============================================================================
export const ComputedSchema = v.object({
  pattern_specification: v.optional(v.nullable(v.union([v.picklist(patternSpecificationList, 'Invalid specification'), v.literal('')]))),
  risk_reward_ratio: v.optional(v.nullable(v.union([v.number('Risk reward ratio must be a number'), v.literal('')]))),
  target: v.optional(v.nullable(v.union([v.number('Target must be a valid number'), v.literal('')]))),
  interim_1: v.optional(v.nullable(v.union([v.number('Interim 1 must be a valid number'), v.literal('')]))),
  interim_2: v.optional(v.nullable(v.union([v.number('Interim 2 must be a valid number'), v.literal('')]))),
  axis: v.optional(v.nullable(v.union([v.number('Axis must be a valid number'), v.literal('')]))),
  time_stop: v.optional(v.nullable(v.any())),
})

// ============================================================================
// Combined Schema
// ============================================================================
export const CreateSetupSchema = v.object({
  ...GeneralSchema.entries,
  ...TechnicalSchema.entries,
  ...ComputedSchema.entries,
})
